import { ShopifyAuth } from 'components/integrations';
import { LoadingSpinner } from 'components/ui';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAddShopifyIntegrationMutation } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { getStoreNameFromShopifyUrl } from 'utils/helpers';
import { useQuery } from 'utils/useQuery';

enum ShopifyIntegrationParams {
  SHOP = 'shop',
  CODE = 'code',
  STATE = 'state',
  HOST = 'host',
  HMAC = 'hmac',
  TIMESTAMP = 'timestamp',
}

const SHOPIFY_LISTING_URL = 'https://apps.shopify.com/segments';

export default function ShopifyAuthentication() {
  const history = useHistory();

  const query = useQuery();

  const shop = query.get(ShopifyIntegrationParams.SHOP);
  const code = query.get(ShopifyIntegrationParams.CODE);
  const state = query.get(ShopifyIntegrationParams.STATE);
  const host = query.get(ShopifyIntegrationParams.HOST);
  const hmac = query.get(ShopifyIntegrationParams.HMAC);
  const timestamp = query.get(ShopifyIntegrationParams.TIMESTAMP);

  const { loggedIn, stores } = useSelector(userDetailsState);

  const [integrateShopify] = useAddShopifyIntegrationMutation();

  const generateShopifyAuthUrl = (storeName: string) => {
    const scopes = [
      'read_orders',
      'read_all_orders',
      'read_products',
      'read_customers',
      'read_inventory',
      'read_analytics',
      'read_checkouts',
      'read_price_rules',
      'read_discounts',
    ];

    const shopifyAuth = new ShopifyAuth({
      scopes: scopes,
      redirectUri: process.env.REACT_APP_SHOPIFY_REDIRECT_URI || '',
      apiKey: process.env.REACT_APP_SHOPIFY_API_KEY || '',
    });

    const userNonce = shopifyAuth.generateNonce();

    localStorage.setItem('user-nonce', userNonce);

    return shopifyAuth.generateAuthUrl(storeName, userNonce);
  };

  useEffect(() => {
    if (!!!shop) {
      window.location.replace(SHOPIFY_LISTING_URL);
      return;
    }

    if (!!!code || !!!state || !!!host || !!!hmac || !!!timestamp) {
      const url = generateShopifyAuthUrl(shop);
      window.location.replace(url);
      return;
    }

    const localStorageNonce = localStorage.getItem('user-nonce');

    if (localStorageNonce !== state) {
      const url = generateShopifyAuthUrl(shop);
      window.location.replace(url);
      return;
    }

    localStorage.removeItem('user-nonce');

    integrateShopify({
      code,
      hmac,
      shop,
      host,
      state,
      timestamp,
    })
      .unwrap()
      .then(() => {
        const userHasStore =
          stores?.length &&
          shop &&
          !!!isEmpty(
            stores.find(
              (store) => store.store_name === getStoreNameFromShopifyUrl(shop)
            )
          );
        if (loggedIn && userHasStore) {
          history.replace({
            pathname: '/dashboard',
            search: `store_name=${getStoreNameFromShopifyUrl(shop)}`,
          });
        } else {
          history.replace({
            pathname: '/integrations/shopify/welcome',
            search: query.toString(),
          });
        }
      })
      .catch(() => {
        window.location.replace(SHOPIFY_LISTING_URL);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid h-screen place-items-center">
      <LoadingSpinner className="w-20 h-20 text-segments-accent-color-orange" />
    </div>
  );
}
